<script>
import api from "../axios.js";
import { useI18n } from "vue-i18n";
const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;

export default {
  data() {
    return {
      companies: [],
      originalCompanies: [],
      expandedPanels: [],
      unsavedFields: {},
      snackbarMessage: "",
      snackbarColor: "success",
      showSnackbar: false,
      availableTimesEnum: [
        "10:00-10:25",
        "10:25-10:50",
        "10:50-11:15",
        "11:15-11:40",
        "11:40-12:05",
        "12:05-12:30",
        "12:30-12:55",
        "12:55-13:20",
        "13:20-13:45",
        "13:45-14:10",
        "14:10-14:35",
        "14:35-15:00",
        "10:30-11:00",
        "11:30-12:00",
        "12:30-13:00",
        "13:30-14:00",
        "14:30-15:00",
      ],
      fileInputModel: null,
      displayedLogoFilenames: {},
    };
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  async mounted() {
    await this.fetchCompanies();
  },
  methods: {
    async fetchCompanies() {
      try {
        const res = await api.get(`${apiBaseUrl}/companies`);
        this.companies = res.data;
        this.originalCompanies = JSON.parse(JSON.stringify(res.data));
        this.unsavedFields = {};
        this.displayedLogoFilenames = {};
        this.companies.forEach((company) => {
          this.displayedLogoFilenames[company._id] =
            company.logo?.fileName || "";
        });
      } catch (error) {
        console.error("Could not fetch company data:", error);
      }
    },

    getOriginalValue(companyId, path) {
      const company = this.originalCompanies.find((c) => c._id === companyId);
      return path.split(".").reduce((obj, key) => obj?.[key], company);
    },

    markFieldUnsaved(companyId, path, value) {
      const originalValue = this.getOriginalValue(companyId, path);
      if (value !== originalValue) {
        if (!this.unsavedFields[companyId]) {
          this.unsavedFields[companyId] = new Set();
        }
        this.unsavedFields[companyId].add(path);
      } else if (this.unsavedFields[companyId]) {
        this.unsavedFields[companyId].delete(path);
        if (this.unsavedFields[companyId].size === 0) {
          delete this.unsavedFields[companyId];
        }
      }
    },

    isFieldUnsaved(companyId, fieldPath) {
      return (
        this.unsavedFields[companyId] &&
        this.unsavedFields[companyId].has(fieldPath)
      );
    },
    getLogoFileName(company) {
      const savedLogo = this.getOriginalValue(company._id, "logo");

      if (company.isRemovingLogo) {
        return `Removing ${savedLogo?.fileName || "fil"}`;
      }

      return savedLogo?.fileName || "";
    },
    onLogoFileChange(id, logo, event) {
      const file = event?.target?.files?.[0];
      if (file) {
        this.fileInputModel = file;
        const company = this.companies.find((c) => c._id === id);
        if (company) {
          company.logo = file;
          this.markFieldUnsaved(id, logo, file);
        }
      }
    },
    onClearFileInput(id) {
      const company = this.companies.find((c) => c._id === id);
      const originalLogo = this.getOriginalValue(id, "logo");
      if (company) {
        company.logo = originalLogo;
        company.isRemovingLogo = false;
        if (this.unsavedFields[id]) {
          this.unsavedFields[id].delete("logo");
          if (this.unsavedFields[id].size === 0) delete this.unsavedFields[id];
        }
      }
      this.fileInputModel = null;
    },
    onMarkLogoForRemoval(id) {
      const company = this.companies.find((c) => c._id === id);
      if (company) {
        company.logo = null; // För att trigga FormData till att skicka borttagningssignal
        company.isRemovingLogo = true;
        this.markFieldUnsaved(id, "logo", null);
      }
    },
    cancelCompanyChanges(id) {
      const index = this.companies.findIndex((c) => c._id === id);
      const original = this.originalCompanies.find((c) => c._id === id);
      if (index !== -1 && original) {
        this.companies[index] = JSON.parse(JSON.stringify(original));
        this.companies[index].isRemovingLogo = false;
        delete this.unsavedFields[id];
        this.fileInputModel = null;

        /*const fileName = original.logo?.fileName || "";
        this.displayedLogoFilenames[id] = fileName; // <-- NY RAD*/
      }
    },

    async saveCompanyChanges(id) {
      const index = this.companies.findIndex((c) => c._id === id);
      if (index === -1) return;

      const company = this.companies[index];
      const formData = new FormData();

      // Enkla textfält
      const textFields = ["name", "founded", "employees", "meetingRoom"];
      textFields.forEach((field) => {
        formData.append(field, company[field]);
      });

      // Fält med språk (sv/en) — ändrat till punktnotation!
      const nestedFields = ["locations", "about", "values"];
      nestedFields.forEach((field) => {
        ["sv", "en"].forEach((lang) => {
          formData.append(`${field}.${lang}`, company[field]?.[lang] || "");
        });
      });

      // Arrayfält med språk — enklare format: field.lang.index
      const arrayFields = ["tags"];
      arrayFields.forEach((field) => {
        ["sv", "en"].forEach((lang) => {
          (company[field]?.[lang] || []).forEach((val, i) => {
            formData.append(`${field}.${lang}.${i}`, val);
          });
        });
      });

      // En annan array (ej språk)
      (company.availableTimes || []).forEach((time, i) => {
        formData.append(`availableTimes.${i}`, time);
      });

      // Fil
      if (company.logo instanceof File) {
        formData.append("logo", company.logo);
      } else if (company.logo === null) {
        // Tydlig signal till backend att logotypen ska tas bort
        formData.append("logo", "");
      }

      try {
        const res = await api.put(`${apiBaseUrl}/companies/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.originalCompanies[index] = JSON.parse(JSON.stringify(res.data));
        this.displayedLogoFilenames[id] = res.data.logo?.fileName || "";
        delete this.unsavedFields[id];
        company.isRemovingLogo = false;
        this.fileInputModel = null;
        this.showNotification(this.t("success.companySaved"), "success");
      } catch (err) {
        console.error(err);
        this.showNotification(this.t("errors.companySaved"), "error");
      }
    },

    showNotification(msg, color) {
      this.snackbarMessage = msg;
      this.snackbarColor = color;
      this.showSnackbar = true;
    },

    onPanelChange(newPanels) {
      // Identifiera stängda paneler och avbryt deras ändringar
      const closedPanels = this.expandedPanels.filter(
        (id) => !newPanels.includes(id)
      );
      closedPanels.forEach(this.cancelCompanyChanges);
      this.expandedPanels = newPanels;
    },

    isUnsaved(companyId) {
      return (
        this.unsavedFields[companyId] && this.unsavedFields[companyId].size > 0
      );
    },
  },
};
</script>

<template>
  <div class="mt-16">
    <h2 class="my-4">
      <router-link to="/add-company">{{ t("company.addCompany") }}</router-link>
    </h2>
    <h2 class="my-4">
      <router-link to="/add-employee">{{
        t("employee.addEmployee")
      }}</router-link>
    </h2>
    <h2 class="my-4">
      <router-link to="/all-meetings">Visa alla möten</router-link>
    </h2>
    <v-expansion-panels
      class="w-75"
      multiple
      v-model="expandedPanels"
      @update:modelValue="onPanelChange"
    >
      <v-expansion-panel
        v-for="company in companies"
        :key="company._id"
        :value="company._id"
      >
        <v-expansion-panel-title>
          <v-row no-gutters class="w-100 align-center">
            <v-col cols="2">
              <v-avatar size="40">
                <v-img
                  :src="getOriginalValue(company._id, 'logo')?.url"
                  alt="Logo"
                />
              </v-avatar>
            </v-col>
            <v-col>
              <span style="display: inline-flex; align-items: center">
                <strong>{{ getOriginalValue(company._id, "name") }}</strong>
                <template v-if="isUnsaved(company._id)">
                  <v-icon color="warning" class="ml-2"
                    >mdi-alert-circle-outline</v-icon
                  >
                  <span class="ml-1">{{ t("profile.unsavedChanges") }}</span>
                </template>
              </span>
            </v-col>
          </v-row>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <v-form @submit.prevent>
            <!-- Gemensamma fält för alla företag -->

            <v-text-field
              :label="t('company.name')"
              v-model="company.name"
              outlined
              dense
              @update:model-value="
                markFieldUnsaved(company._id, 'name', $event)
              "
              color="primary"
            />
            <em v-if="isFieldUnsaved(company._id, 'name')" class="unsaved-text">
              {{ t("profile.unsavedChanges") }}
            </em>

            <v-text-field
              :label="t('company.founded')"
              v-model="company.founded"
              outlined
              dense
              @update:model-value="
                markFieldUnsaved(company._id, 'founded', $event)
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'founded')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <v-text-field
              :label="t('company.employees')"
              v-model="company.employees"
              outlined
              dense
              @input="
                markFieldUnsaved(company._id, 'employees', company.employees)
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'employees')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <!-- Filnamnsfält + ta bort knapp -->
            <v-row class="align-center" dense>
              <v-col cols="8">
                <p>{{ getLogoFileName(company) }}</p>
              </v-col>
              <v-col cols="4" class="text-right">
                <v-btn
                  v-if="
                    getOriginalValue(company._id, 'logo') &&
                    !company.isRemovingLogo
                  "
                  color="red"
                  @click="onMarkLogoForRemoval(company._id)"
                >
                  {{ t("common.remove") }}
                </v-btn>
              </v-col>
            </v-row>

            <v-file-input
              v-model="fileInputModel"
              label="Logo"
              outlined
              dense
              show-size
              accept="image/*"
              @change="onLogoFileChange(company._id, 'logo', $event)"
              @click:clear="onClearFileInput(company._id)"
              color="primary"
            />

            <em v-if="isFieldUnsaved(company._id, 'logo')" class="unsaved-text">
              {{ t("profile.unsavedChanges") }}
            </em>

            <v-text-field
              :label="t('company.locations') + ' (sv)'"
              v-model="company.locations.sv"
              outlined
              dense
              @input="
                markFieldUnsaved(
                  company._id,
                  'locations.sv',
                  company.locations.sv
                )
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'locations.sv')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <v-text-field
              :label="t('company.locations') + ' (en)'"
              v-model="company.locations.en"
              outlined
              dense
              @input="
                markFieldUnsaved(
                  company._id,
                  'locations.en',
                  company.locations.en
                )
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'locations.en')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <v-textarea
              :label="t('company.about') + ' (sv)'"
              v-model="company.about.sv"
              outlined
              rows="2"
              @input="
                markFieldUnsaved(company._id, 'about.sv', company.about.sv)
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'about.sv')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <v-textarea
              :label="t('company.about') + ' (en)'"
              v-model="company.about.en"
              outlined
              rows="2"
              @input="
                markFieldUnsaved(company._id, 'about.en', company.about.en)
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'about.en')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <v-textarea
              :label="t('company.values') + ' (sv)'"
              v-model="company.values.sv"
              outlined
              rows="2"
              @input="
                markFieldUnsaved(company._id, 'values.sv', company.values.sv)
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'values.sv')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <v-textarea
              :label="t('company.values') + ' (en)'"
              v-model="company.values.en"
              outlined
              rows="2"
              @input="
                markFieldUnsaved(company._id, 'values.en', company.values.en)
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'values.en')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <!-- Tags (svenska) -->
            <v-combobox
              v-model="company.tags.sv"
              :label="t('company.tags') + ' (sv)'"
              multiple
              outlined
              chips
              deletable-chips
              small-chips
              @update:model-value="
                markFieldUnsaved(company._id, 'tags.sv', $event)
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'tags.sv')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <!-- Tags (engelska) -->
            <v-combobox
              v-model="company.tags.en"
              :label="t('company.tags') + ' (en)'"
              multiple
              outlined
              chips
              deletable-chips
              small-chips
              @update:model-value="
                markFieldUnsaved(company._id, 'tags.en', $event)
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'tags.en')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <!-- Bokningsbara tider :label ska kanske inte ha Tillgängliga tider? Vad gör den? -->
            <v-combobox
              v-if="company.contactMeeting === true"
              v-model="company.availableTimes"
              :label="t('company.availableTimes')"
              multiple
              outlined
              chips
              deletable-chips
              small-chips
              :items="availableTimesEnum"
              @update:model-value="
                markFieldUnsaved(company._id, 'availableTimes', $event)
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'availableTimes')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <v-text-field
              v-if="company.contactMeeting === true"
              :label="t('company.meetingRoom')"
              v-model="company.meetingRoom"
              outlined
              dense
              @update:model-value="
                markFieldUnsaved(company._id, 'meetingRoom', $event)
              "
              color="primary"
            />
            <em
              v-if="isFieldUnsaved(company._id, 'meetingRoom')"
              class="unsaved-text"
            >
              {{ t("profile.unsavedChanges") }}
            </em>

            <!-- Action Buttons -->
            <v-row class="mt-4" v-if="isUnsaved(company._id)">
              <v-col cols="6">
                <v-btn color="primary" @click="saveCompanyChanges(company._id)">
                  {{ t("common.save") }}
                </v-btn>
              </v-col>
              <v-col cols="6">
                <v-btn color="grey" @click="cancelCompanyChanges(company._id)">
                  {{ t("common.cancel") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Snackbar -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      timeout="3000"
      location="top"
      elevation="10"
    >
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<style scoped>
.mt-16 {
  margin-top: 64px;
}
.unsaved-text {
  color: #757575; /* grå färg */
  font-size: 0.75rem;
  margin-top: -20px;
  margin-bottom: 16px;
  display: block;
}
</style>
